import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { authApi } from './authApi';

const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';
const DEFAULT_TIMEOUT_MS = 30000;
const CONFIGURED_TIMEOUT = Number.parseInt(process.env.REACT_APP_HTTP_TIMEOUT_MS || '', 10);
const TIMEOUT_MS = Number.isFinite(CONFIGURED_TIMEOUT) && CONFIGURED_TIMEOUT > 0 ? CONFIGURED_TIMEOUT : DEFAULT_TIMEOUT_MS;

// Token management
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (error?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Temporary debug log
// eslint-disable-next-line no-console
console.warn('[gatewayApi] baseURL:', GATEWAY_BASE_URL, '| x-api-key set:', Boolean(API_KEY), '| timeout(ms):', TIMEOUT_MS);

const gatewayClient: AxiosInstance = axios.create({
	baseURL: GATEWAY_BASE_URL,
	timeout: TIMEOUT_MS,
	withCredentials: false,
	headers: {
		'Content-Type': 'application/json',
		...(API_KEY ? { 'x-api-key': API_KEY } : {}),
	},
});

// Attach Authorization header if a token is available
gatewayClient.interceptors.request.use((config) => {
	const token = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
	if (token) {
		config.headers = config.headers || {};
		(config.headers as any)['Authorization'] = token;
	}
	
	// Add request logging for debugging
	console.log('[gatewayApi] Request:', {
		method: config.method?.toUpperCase(),
		url: config.url,
		baseURL: config.baseURL,
		fullURL: `${config.baseURL}${config.url}`,
		headers: config.headers,
		data: config.data,
		params: config.params,
		timestamp: new Date().toISOString()
	});
	
	return config;
});

// Response interceptor to handle token expiration and automatic refresh
gatewayClient.interceptors.response.use(
	(response: AxiosResponse) => {
		// Add response logging for debugging
		console.log('[gatewayApi] Response:', {
			method: response.config.method?.toUpperCase(),
			url: response.config.url,
			status: response.status,
			statusText: response.statusText,
			data: response.data,
			headers: response.headers,
			timestamp: new Date().toISOString()
		});
		
		return response;
	},
	async (error) => {
		// Add error logging for debugging
		console.error('[gatewayApi] Error Response:', {
			method: error.config?.method?.toUpperCase(),
			url: error.config?.url,
			status: error.response?.status,
			statusText: error.response?.statusText,
			data: error.response?.data,
			message: error.message,
			headers: error.config?.headers,
			timestamp: new Date().toISOString()
		});
		
		const originalRequest = error.config;

		// Handle 401 Unauthorized errors (token expired)
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				// If already refreshing, queue this request
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then(token => {
					originalRequest.headers['Authorization'] = token;
					return gatewayClient(originalRequest);
				}).catch(err => {
					return Promise.reject(err);
				});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				// Attempt to refresh the session
				const refreshResponse = await authApi.refreshSession();
				
				if (refreshResponse && refreshResponse.user) {
					// Update the token in storage
					const newToken = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
					if (newToken) {
						// Update the original request with new token
						originalRequest.headers['Authorization'] = newToken;
						
						// Process queued requests
						processQueue(null, newToken);
						
						// Retry the original request
						return gatewayClient(originalRequest);
					}
				}
			} catch (refreshError) {
				// Refresh failed, redirect to login
				processQueue(refreshError, null);
				
				// Clear stored tokens
				localStorage.removeItem('jwt');
				sessionStorage.removeItem('jwt');
				
				// Redirect to login page
				window.location.href = '/login';
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		// Handle 403 Forbidden errors (might be token-related)
		if (error.response?.status === 403) {
			// Log the 403 error for debugging
			console.warn('[gatewayApi] 403 Forbidden error:', error.response?.data);
			
			// Check if it's a token issue by attempting a refresh
			const token = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
			if (token && !originalRequest._retry) {
				originalRequest._retry = true;
				
				try {
					// Attempt to refresh the session
					const refreshResponse = await authApi.refreshSession();
					
					if (refreshResponse && refreshResponse.user) {
						const newToken = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
						if (newToken) {
							originalRequest.headers['Authorization'] = newToken;
							return gatewayClient(originalRequest);
						}
					}
				} catch (refreshError) {
					console.warn('[gatewayApi] Token refresh failed for 403 error:', refreshError);
				}
			}
		}

		return Promise.reject(error);
	}
);

export const gatewayApi = {
	getRaw: <T = any>(url: string, params?: any, config?: any): Promise<AxiosResponse<T>> =>
		gatewayClient.get<T>(url, { ...(config || {}), params }),
	postRaw: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> =>
		gatewayClient.post<T>(url, data, config),
	putRaw: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> =>
		gatewayClient.put<T>(url, data, config),
	deleteRaw: <T = any>(url: string, config?: any): Promise<AxiosResponse<T>> =>
		gatewayClient.delete<T>(url, config),

	get: <T>(url: string, params?: any, config?: any): Promise<T> =>
		gatewayClient.get<T>(url, { ...(config || {}), params }).then(r => r.data),
	post: <T>(url: string, data?: any, config?: any): Promise<T> =>
		gatewayClient.post<T>(url, data, config).then(r => r.data),
	put: <T>(url: string, data?: any, config?: any): Promise<T> =>
		gatewayClient.put<T>(url, data, config).then(r => r.data),
	delete: <T>(url: string, config?: any): Promise<T> =>
		gatewayClient.delete<T>(url, config).then(r => r.data),
	getBlob: (url: string, params?: any, config?: any): Promise<Blob> =>
		gatewayClient.get(url, { ...(config || {}), params, responseType: 'blob' }).then(r => r.data),
	upload: <T>(url: string, formData: FormData, config?: any): Promise<T> =>
		gatewayClient
			.post<T>(url, formData, { ...(config || {}), headers: { 'Content-Type': 'multipart/form-data', ...((config && config.headers) || {}) } })
			.then(r => r.data),
};

export default gatewayClient; 